import { useInView } from '../../../hooks/useInView';

export interface ServiceOption {
  id: string;
  label: string;
  icon: string;
  description: string;
  baseMin: number;
  baseMax: number;
  unit: string;
  notApplicable?: string[];
  fixedPrice?: boolean;
}

export const SERVICES: ServiceOption[] = [
  {
    id: 'revision',
    label: 'Revisión periódica',
    icon: 'ri-shield-check-line',
    description: 'Aceite, filtros, frenos, luces, niveles…',
    baseMin: 49,
    baseMax: 99,
    unit: '€',
    notApplicable: [],
  },
  {
    id: 'aceite',
    label: 'Cambio de aceite',
    icon: 'ri-drop-line',
    description: 'Cambio de aceite + filtro de aceite',
    baseMin: 49,
    baseMax: 79,
    unit: '€',
  },
  {
    id: 'diagnostico',
    label: 'Diagnóstico electrónico',
    icon: 'ri-search-eye-line',
    description: 'Lectura de errores OBD, análisis de sensores',
    baseMin: 29,
    baseMax: 59,
    unit: '€',
  },
  {
    id: 'frenos_pastillas',
    label: 'Frenos – Pastillas',
    icon: 'ri-stop-circle-line',
    description: 'Sustitución pastillas eje delantero',
    baseMin: 79,
    baseMax: 139,
    unit: '€',
  },
  {
    id: 'frenos_discos',
    label: 'Frenos – Discos + Pastillas',
    icon: 'ri-settings-4-line',
    description: 'Cambio completo discos y pastillas delanteros',
    baseMin: 149,
    baseMax: 259,
    unit: '€',
  },
  {
    id: 'suspension',
    label: 'Suspensión (amortiguadores)',
    icon: 'ri-car-line',
    description: 'Cambio 2 amortiguadores + revisión rótulas',
    baseMin: 159,
    baseMax: 299,
    unit: '€',
    notApplicable: ['moto'],
  },
  {
    id: 'clima',
    label: 'Climatización / AC',
    icon: 'ri-temp-cold-line',
    description: 'Recarga gas, limpieza circuito, diagnóstico',
    baseMin: 59,
    baseMax: 99,
    unit: '€',
    notApplicable: ['moto'],
  },
  {
    id: 'distribucion',
    label: 'Correa de distribución',
    icon: 'ri-loop-right-line',
    description: 'Cambio correa, tensores y bomba de agua',
    baseMin: 199,
    baseMax: 449,
    unit: '€',
    notApplicable: ['moto'],
  },
  {
    id: 'preitv',
    label: 'Pre-ITV',
    icon: 'ri-file-check-2-line',
    description: 'Revisión exhaustiva antes de pasar la ITV',
    baseMin: 29,
    baseMax: 49,
    unit: '€',
    notApplicable: ['moto'],
  },
  {
    id: 'dpf',
    label: 'Filtro de partículas (DPF)',
    icon: 'ri-filter-3-line',
    description: 'Limpieza ultrasónica o regeneración del DPF',
    baseMin: 79,
    baseMax: 169,
    unit: '€',
    notApplicable: ['moto'],
  },
  {
    id: 'caja',
    label: 'Caja de cambios',
    icon: 'ri-settings-2-line',
    description: 'Mantenimiento: aceite ATF, filtros, revisión',
    baseMin: 69,
    baseMax: 149,
    unit: '€',
    notApplicable: ['moto'],
  },
  {
    id: 'llave',
    label: 'Copia / Reparación de llave',
    icon: 'ri-key-2-line',
    description: 'Duplicado con chip transponder o mando',
    baseMin: 25,
    baseMax: 95,
    unit: '€',
    fixedPrice: true,
  },
];

interface ServiceStepProps {
  selected: ServiceOption | null;
  onSelect: (s: ServiceOption) => void;
  vehicleType: string;
}

const ServiceStep = ({ selected, onSelect, vehicleType }: ServiceStepProps) => {
  const [ref, inView] = useInView(0.05);

  const available = SERVICES.filter(
    (s) => !s.notApplicable?.includes(vehicleType),
  );

  return (
    <div>
      <div className="mb-8 text-center">
        <p className="text-zinc-400 text-sm">
          ¿Qué servicio necesitas?{' '}
          <span className="text-yellow-400">Selecciona uno para continuar.</span>
        </p>
      </div>
      <div
        ref={ref}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        {available.map((s, i) => {
          const isSelected = selected?.id === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onSelect(s)}
              className={`group text-left p-4 border transition-all duration-300 cursor-pointer ${
                isSelected
                  ? 'border-yellow-400 bg-yellow-400/8'
                  : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-600'
              } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${i * 0.04}s` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-9 h-9 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                    isSelected ? 'bg-yellow-400 text-black' : 'bg-zinc-800 text-zinc-400 group-hover:bg-zinc-700'
                  }`}
                >
                  <i className={`${s.icon} text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className={`text-xs font-bold uppercase tracking-wide whitespace-nowrap ${isSelected ? 'text-yellow-400' : 'text-white'}`}>
                      {s.label}
                    </span>
                    {isSelected && (
                      <i className="ri-checkbox-circle-fill text-yellow-400 text-sm flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-zinc-500 text-[11px] leading-relaxed line-clamp-2">{s.description}</p>
                  <p className={`text-[11px] font-semibold mt-1.5 ${isSelected ? 'text-yellow-300' : 'text-zinc-500'}`}>
                    {s.fixedPrice ? `${s.baseMin} – ${s.baseMax} ${s.unit}` : `Desde ${s.baseMin} ${s.unit}`}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceStep;
